import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CartState } from "./cart.state";
import {
  getCart,
  getCartCount,
  mergeCart,
  addToCart,
  updateCartQuantity,
  removeCartItem,
  clearCartThunk,
  toggleCartItem,
  toggleSelectAll,
  selectBySeller,
  applyDiscount,
  removeDiscount,
} from "./cart.thunk";
import { logoutUser } from "../auth/auth.thunk";
import { logout } from "../auth/auth.slice";
import {
  CartResponse,
  CartCountResponse,
  AddToCartResponse,
  UpdateCartQuantityResponse,
  RemoveCartItemResponse,
  SelectBySellerResponse,
  ApplyDiscountResponse,
  RemoveDiscountResponse,
  ApiCartItem,
} from "./cart.types";

const initialState: CartState = {
  data: null,
  status: "idle",
  error: null,
  count: 0,
  countStatus: "idle",
  itemUpdating: null,
  itemToggling: null,
  itemRemoving: null,
  isTogglingAll: false,
  sellerToggling: null,
  isApplyingDiscount: false,
  isRemovingDiscount: false,
  isClearingCart: false,
};

export const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    resetCart: (state) => {
      state.data = null;
      state.count = 0;
      state.status = "idle";
      state.countStatus = "idle";
      state.error = null;
      state.itemUpdating = null;
      state.itemToggling = null;
      state.itemRemoving = null;
      state.isTogglingAll = false;
      state.sellerToggling = null;
      state.isApplyingDiscount = false;
      state.isRemovingDiscount = false;
      state.isClearingCart = false;
    },
    updateCartCount: (state, action: PayloadAction<number>) => {
      state.count = action.payload;
    },
    setItemUpdating: (state, action: PayloadAction<number | null>) => {
      state.itemUpdating = action.payload;
    },
    setItemToggling: (state, action: PayloadAction<number | null>) => {
      state.itemToggling = action.payload;
    },
    setItemRemoving: (state, action: PayloadAction<number | null>) => {
      state.itemRemoving = action.payload;
    },
    setTogglingAll: (state, action: PayloadAction<boolean>) => {
      state.isTogglingAll = action.payload;
    },
    setSellerToggling: (state, action: PayloadAction<number | null>) => {
      state.sellerToggling = action.payload;
    },
    updateItemQuantityOptimistic: (
      state,
      action: PayloadAction<{ itemId: number; quantity: number }>,
    ) => {
      if (state.data) {
        state.data.grouped_items.forEach((group) => {
          const item = group.items.find(
            (item) => item.id === action.payload.itemId,
          );
          if (item) {
            item.quantity = action.payload.quantity;
          }
        });
      }
      state.itemUpdating = action.payload.itemId;
    },
    rollbackItemUpdate: (state) => {
      state.itemUpdating = null;
    },
    syncCartItem: (state, action: PayloadAction<ApiCartItem>) => {
      if (state.data) {
        state.data.grouped_items.forEach((group) => {
          const itemIndex = group.items.findIndex(
            (item) => item.id === action.payload.id,
          );
          if (itemIndex !== -1) {
            group.items[itemIndex] = {
              ...group.items[itemIndex],
              ...action.payload,
            };
          }
        });
      }
      state.itemUpdating = null;
    },
  },
  extraReducers: (build) => {
    build
      // GET CART
      .addCase(getCart.pending, (state) => {
        if (!state.data) {
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(getCart.fulfilled, (state, action: PayloadAction<CartResponse>) => {
        state.status = "success";
        state.data = action.payload.data;
        state.count = action.payload.data.cart_info.total_quantity;
        state.itemUpdating = null;
        state.itemToggling = null;
        state.itemRemoving = null;
        state.isTogglingAll = false;
        state.sellerToggling = null;
      })
      .addCase(getCart.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.status = "error";
        state.error = action.payload ?? "Sepet yüklenirken bir hata oluştu.";
        state.itemUpdating = null;
        state.itemToggling = null;
        state.itemRemoving = null;
        state.isTogglingAll = false;
        state.sellerToggling = null;
      })

      // GET CART COUNT
      .addCase(getCartCount.pending, (state) => {
        state.countStatus = "loading";
      })
      .addCase(getCartCount.fulfilled, (state, action: PayloadAction<CartCountResponse>) => {
        state.countStatus = "success";
        state.count = action.payload.data.count;
      })
      .addCase(getCartCount.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.countStatus = "error";
        state.error = action.payload ?? "Sepet sayısı yüklenirken bir hata oluştu.";
      })

      // MERGE CART
      .addCase(mergeCart.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(mergeCart.fulfilled, (state) => {
        state.status = "success";
      })
      .addCase(mergeCart.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.status = "error";
        state.error = action.payload ?? "Sepet birleştirilirken bir hata oluştu.";
      })

      // ADD TO CART
      .addCase(addToCart.pending, (state) => {
        state.error = null;
      })
      .addCase(addToCart.fulfilled, (state, action: PayloadAction<AddToCartResponse>) => {
        state.count = action.payload.data.cart_count;
        state.status = "success";
      })
      .addCase(addToCart.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.error = action.payload ?? "Ürün sepete eklenirken bir hata oluştu.";
      })

      // UPDATE QUANTITY
      .addCase(updateCartQuantity.fulfilled, (state, action: PayloadAction<UpdateCartQuantityResponse>) => {
        state.count = action.payload.data.cart_count;
        if (state.data) {
          state.data.grouped_items.forEach((group) => {
            const itemIndex = group.items.findIndex(
              (item) => item.id === action.payload.data.cart_item.id,
            );
            if (itemIndex !== -1) {
              group.items[itemIndex] = {
                ...group.items[itemIndex],
                ...action.payload.data.cart_item,
              };
            }
          });
          if (action.payload.data.cart_box) {
            state.data.cart_box = action.payload.data.cart_box;
          }
        }
        state.itemUpdating = null;
      })
      .addCase(updateCartQuantity.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.itemUpdating = null;
        state.error = action.payload ?? "Miktar güncellenirken bir hata oluştu.";
      })

      // REMOVE ITEM
      .addCase(removeCartItem.fulfilled, (state, action: PayloadAction<RemoveCartItemResponse>) => {
        state.count = action.payload.data.cart_count;
        // Liste güncellemesi fetchCart() (getCart.fulfilled) ile yapılır;
        // burada item kaldırılmaz — dialog kapanmadan önce liste değişmesin
        state.itemRemoving = null;
      })
      .addCase(removeCartItem.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.itemRemoving = null;
        state.error = action.payload ?? "Ürün sepetten çıkarılırken bir hata oluştu.";
      })

      // CLEAR CART
      .addCase(clearCartThunk.pending, (state) => {
        state.isClearingCart = true;
        state.error = null;
      })
      .addCase(clearCartThunk.fulfilled, (state) => {
        state.data = null;
        state.count = 0;
        state.isClearingCart = false;
      })

      // TOGGLE CART ITEM
      .addCase(toggleCartItem.pending, (state, action) => {
        state.itemToggling = action.meta.arg;
      })
      .addCase(toggleCartItem.fulfilled, (state) => {
        state.itemToggling = null;
      })
      .addCase(toggleCartItem.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.itemToggling = null;
        state.error = action.payload ?? "Ürün seçimi değiştirilirken bir hata oluştu.";
      })

      // TOGGLE SELECT ALL
      .addCase(toggleSelectAll.pending, (state) => {
        state.isTogglingAll = true;
      })
      .addCase(toggleSelectAll.fulfilled, (state) => {
        state.isTogglingAll = false;
      })
      .addCase(toggleSelectAll.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.isTogglingAll = false;
        state.error = action.payload ?? "Tümünü seç/kaldır işlemi sırasında bir hata oluştu.";
      })

      // SELECT BY SELLER
      .addCase(selectBySeller.pending, (state, action) => {
        state.sellerToggling = action.meta.arg.seller_id;
        state.error = null;
      })
      .addCase(selectBySeller.fulfilled, (state, action: PayloadAction<SelectBySellerResponse>) => {
        state.data = action.payload.data;
        state.count = action.payload.data.cart_info.total_quantity;
        state.sellerToggling = null;
      })
      .addCase(selectBySeller.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.sellerToggling = null;
        state.error = action.payload ?? "Satıcı seçimi değiştirilirken bir hata oluştu.";
      })

      // APPLY DISCOUNT
      .addCase(applyDiscount.pending, (state) => {
        state.isApplyingDiscount = true;
        state.error = null;
      })
      .addCase(applyDiscount.fulfilled, (state, action: PayloadAction<ApplyDiscountResponse>) => {
        state.data = action.payload.data;
        state.count = action.payload.data.cart_info.total_quantity;
        state.isApplyingDiscount = false;
      })
      .addCase(applyDiscount.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.isApplyingDiscount = false;
        state.error = action.payload ?? "İndirim kodu uygulanırken bir hata oluştu.";
      })

      // REMOVE DISCOUNT
      .addCase(removeDiscount.pending, (state) => {
        state.isRemovingDiscount = true;
        state.error = null;
      })
      .addCase(removeDiscount.fulfilled, (state, action: PayloadAction<RemoveDiscountResponse>) => {
        state.data = action.payload.data;
        state.count = action.payload.data.cart_info.total_quantity;
        state.isRemovingDiscount = false;
      })
      .addCase(removeDiscount.rejected, (state, action: PayloadAction<string | undefined>) => {
        state.isRemovingDiscount = false;
        state.error = action.payload ?? "İndirim kaldırılırken bir hata oluştu.";
      })

      // LOGOUT — reset cart state
      .addCase(logoutUser.fulfilled, (state) => {
        Object.assign(state, initialState);
      })
      .addCase(logout, (state) => {
        Object.assign(state, initialState);
      });
  },
});

export const {
  resetCart,
  updateCartCount,
  setItemUpdating,
  setItemToggling,
  setItemRemoving,
  setTogglingAll,
  setSellerToggling,
  updateItemQuantityOptimistic,
  rollbackItemUpdate,
  syncCartItem,
} = cartSlice.actions;

export default cartSlice.reducer;